import { useContext, useEffect, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { Context } from '../../../AuthContext';
import { useCategory } from '../../../CategoryContext';

/**
 * Read-only iframe of the vertical's selected Google calendar, for use on the
 * Vertical Workbench main tab. Mirrors the iframe rendered by the My Calendar
 * tab's VerticalCalendar component, minus the owner/editor controls.
 */
const EmbeddedVerticalCalendar = ({ verticalId, onOpenVerticalCalendar }) => {
  const { user } = useContext(Context);
  const { verticals } = useCategory();
  const [phase, setPhase] = useState('loading'); // loading | empty | ready | error
  const [embedUrl, setEmbedUrl] = useState('');

  const myVertical = (verticals || []).find((v) => v.id === verticalId);
  const isOwner = myVertical?.status === 'Created';

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!user || !verticalId) return;
      try {
        const token = await user.getIdToken();
        const res = await fetch(
          `${
            process.env.REACT_APP_FLASK_WEBSERVER
          }oauth/google/vertical-calendar?vertical_id=${encodeURIComponent(verticalId)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        if (!res.ok) throw new Error(`vertical_calendar_${res.status}`);
        const vc = await res.json();
        if (!vc?.calendar_id) {
          setPhase('empty');
          return;
        }
        const params = new URLSearchParams();
        params.append('ctz', Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
        params.append('mode', 'MONTH');
        params.append('wkst', '1');
        params.append('showTitle', '0');
        params.append('showPrint', '0');
        params.append('src', vc.calendar_id);
        if (vc.backgroundColor) params.append('color', vc.backgroundColor);
        setEmbedUrl(`https://calendar.google.com/calendar/embed?${params.toString()}`);
        setPhase('ready');
      } catch (_) {
        if (!cancelled) setPhase('error');
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [user, verticalId]);

  const wrap = (children) => (
    <div
      style={{
        height: '100%',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: 16,
        boxSizing: 'border-box',
        overflowY: 'auto',
      }}
    >
      {children}
    </div>
  );

  if (phase === 'loading') return wrap(<CircularProgress size={24} />);

  if (phase === 'empty') {
    if (isOwner) {
      return wrap(
        <div>
          <p style={{ margin: 0, marginBottom: 12, color: '#475467', fontSize: 13 }}>
            No calendar has been set up for this vertical yet. Pick a Google calendar to share
            with your collaborators.
          </p>
          {onOpenVerticalCalendar && (
            <Button
              variant="contained"
              onClick={onOpenVerticalCalendar}
              sx={{ textTransform: 'none' }}
            >
              Go to Vertical Calendar
            </Button>
          )}
        </div>
      );
    }
    return wrap(
      <p style={{ margin: 0, color: '#667085', fontSize: 13 }}>
        The owner hasn't set up a calendar for this vertical yet.
      </p>
    );
  }

  if (phase === 'error') {
    return wrap(
      <p style={{ margin: 0, color: '#B42318', fontSize: 13 }}>
        Could not load the vertical calendar. Please try again later.
      </p>
    );
  }

  return (
    <iframe
      title="Vertical Calendar"
      src={embedUrl}
      style={{ border: 0, width: '100%', height: '100%' }}
      frameBorder="0"
      scrolling="no"
    />
  );
};

export default EmbeddedVerticalCalendar;
